import { Briefcase02, Building02, File06, Mail01 } from '@untitledui/icons';
import { useCallback } from 'react';

import { useEmailCompose } from '@/contexts/EmailComposeContext';
import { Button } from '@/lib/base/buttons/button';
import { CrmObjectAvatar } from '@/lib/crm/components/CrmObjectAvatar';
import { TextValueLabel } from '@/lib/crm/components/properties/labels/TextValueLabel';
import { useCrmObjectSnapshot } from '@/lib/crm/hooks/useCrmObjectSnapshot';
import { useNoteTabActions } from '@/lib/crm/record/notes/useNoteTabActions';
import { getEntityPrimaryEmail } from '@/lib/crm/store/crmObjectStore';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

const DRAFT_EMAIL_LABEL = 'Draft email';

const COMPANY_ASSOCIATION_TYPE = 'ahoy_contact_to_company';

export interface EntityHoverContactPreviewProps {
  /** Entity must have entity_type_name === 'ahoy_contact'. Caller is responsible for the guard. */
  object: CrmObject;
  orgId: number;
}

/**
 * Rich hover preview for `ahoy_contact` entities — shows avatar + name +
 * email in a header row, followed by a property section (job title, company)
 * and a "Draft email" action.
 *
 * NOTE: An earlier version reused `CrmObjectCard` + `contactCardConfig` per
 * Issue #5483 Decision #1. That decision is overridden here for visual
 * quality reasons — `CrmObjectCard` adds its own internal `border + bg` chrome
 * which produces a card-on-card effect against the popover chrome added in
 * `EntityHoverPopover`. The hover surface needs an Attio-style flat header +
 * property-row layout, not a pipeline-card-style nested container. The
 * underlying field set (display name, email, job title, company) is preserved.
 */
export function EntityHoverContactPreview({
  object,
  orgId,
}: EntityHoverContactPreviewProps): React.ReactElement {
  const displayName = getDisplayName(object);
  // `ahoy_email` is a multi-value property, so its serialized value can be an
  // array. Route through the shared extractor (the single source of truth used
  // by every other email entry point) to get one normalized, trimmed address —
  // never a nested array — before handing it to the composer.
  const email = getEntityPrimaryEmail(object);
  const jobTitle = getPropertyValue<string>(object.properties?.ahoy_job_title);
  const companyAssociation = (object.associations ?? []).find(
    a => a.association_type_name === COMPANY_ASSOCIATION_TYPE
  );

  // Same hook as the record header's "Add note" — one composer, one gate.
  const { startCreatingNote, canCreateNote } = useNoteTabActions(
    object.entity_id,
    object.entity_type_name
  );
  const { openEmailCompose } = useEmailCompose();

  const handleSendEmail = useCallback(() => {
    openEmailCompose({
      to: email ? [email] : undefined,
      recipientEntity: {
        entityId: object.entity_id,
        entityTypeName: object.entity_type_name,
        displayName,
      },
    });
  }, [
    openEmailCompose,
    email,
    object.entity_id,
    object.entity_type_name,
    displayName,
  ]);

  const hasProperties = Boolean(jobTitle) || Boolean(companyAssociation);

  return (
    <div className='flex flex-col'>
      {/* Header — avatar + name + email */}
      <div className='flex items-center gap-2.5 p-3'>
        <CrmObjectAvatar object={object} size='xxs' />
        <div className='flex flex-col min-w-0 flex-1 gap-0.5'>
          <span className='text-[13px] font-semibold leading-tight truncate text-[var(--color-text-primary)]'>
            {displayName}
          </span>
          {email && (
            <span className='text-xs truncate text-[var(--color-text-secondary)]'>
              {email}
            </span>
          )}
        </div>
      </div>

      {/* Properties — job title, company */}
      {hasProperties && (
        <div className='flex flex-col gap-1.5 px-3 pb-3 pt-2.5 border-t border-[var(--color-border-secondary)]'>
          {jobTitle && (
            <PropertyRow
              icon={
                <Briefcase02 className='size-3.5 text-[var(--color-fg-quaternary)]' />
              }
              label='Job title'
              value={
                <TextValueLabel
                  value={jobTitle}
                  orgId={orgId}
                  fontSize='table'
                  className='font-medium text-[var(--color-text-primary)]'
                  truncate
                />
              }
            />
          )}
          {companyAssociation && (
            <PropertyRow
              icon={
                <Building02 className='size-3.5 text-[var(--color-fg-quaternary)]' />
              }
              label='Works at'
              value={
                <CompanyValue
                  entityId={companyAssociation.target_entity_id}
                  orgId={orgId}
                />
              }
            />
          )}
        </div>
      )}

      {/* Footer — actions. The button uses `!` overrides on top of `size='sm'`
          to land at ~28px tall / 12px text, which matches the card's compact
          rhythm. Without these, the default `sm` button (36px tall / 14px
          semibold) dominates the popover visually. */}
      <div className='flex items-center gap-2 px-3 pb-3 pt-2.5 border-t border-[var(--color-border-secondary)]'>
        <Button
          color='primary'
          size='sm'
          iconLeading={Mail01}
          onClick={handleSendEmail}
          className='!text-xs !px-2.5 !py-1.5'
        >
          {DRAFT_EMAIL_LABEL}
        </Button>
        {canCreateNote && (
          <Button
            color='secondary'
            size='sm'
            iconLeading={File06}
            onClick={startCreatingNote}
            className='!text-xs !px-2.5 !py-1.5'
          >
            Add note
          </Button>
        )}
      </div>
    </div>
  );
}

interface PropertyRowProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}

function PropertyRow({
  icon,
  label,
  value,
}: PropertyRowProps): React.ReactElement {
  return (
    <div className='flex items-center gap-2 min-w-0'>
      <div className='flex items-center gap-1.5 w-20 shrink-0 text-[var(--color-text-secondary)]'>
        {icon}
        <span className='text-[11px] font-medium'>{label}</span>
      </div>
      <div className='min-w-0 flex-1 truncate'>{value}</div>
    </div>
  );
}

interface CompanyValueProps {
  entityId: string;
  orgId: number;
}

/**
 * Renders an associated company's name with avatar inline.
 *
 * Imported here as a tiny child component (instead of using `EntityBadgeById`)
 * to avoid a circular dependency: `EntityBadgeById` -> `HoverPreviewBadge` ->
 * `renderHoverBody` -> `EntityHoverContactPreview`. ESM tolerates the cycle
 * but can resolve named exports to `undefined` depending on order, which
 * produced "Element type is invalid" runtime errors during integration.
 */
function CompanyValue({
  entityId,
  orgId,
}: CompanyValueProps): React.ReactElement {
  const company = useCrmObjectSnapshot(entityId, orgId);

  // Render a height-stable skeleton while the company snapshot loads, with
  // the SAME row geometry as the loaded state. The previous implementation
  // returned a single-line `—` (~18px tall) and then swapped to avatar +
  // name (~32px tall) — that vertical delta caused the popover to grow on
  // open and visibly reflowed the page below it. Matching dimensions keeps
  // popover height stable throughout the load.
  if (!company) {
    return (
      <div className='flex items-center gap-1.5 min-w-0 h-8'>
        <div
          className='size-8 rounded-md bg-[var(--color-bg-secondary)] animate-pulse shrink-0'
          aria-hidden
        />
        <div
          className='h-3 w-20 rounded bg-[var(--color-bg-secondary)] animate-pulse'
          aria-hidden
        />
      </div>
    );
  }
  return (
    <div className='flex items-center gap-1.5 min-w-0 h-8'>
      <CrmObjectAvatar object={company} size='xxs' />
      <span className='text-[13px] font-medium text-[var(--color-text-primary)] truncate'>
        {getDisplayName(company)}
      </span>
    </div>
  );
}
